/**
 * Access-token refresh utility.
 *
 * Two strategies, both used:
 *   1. PROACTIVE: before each request, decode the access token's `exp`
 *      claim. If it's expiring within `EXPIRY_BUFFER_MS`, kick off a
 *      refresh and use the new token instead. Avoids the 401-retry round
 *      trip in the common case.
 *   2. REACTIVE: if a request fails with a JWT-expired error anyway
 *      (clock skew, server-side revoke, etc.), force one refresh and
 *      retry the request once. If retry still fails, surface the error.
 *
 * A single in-flight refresh is coalesced: many concurrent requests that
 * all decide "token is stale" will share one network call rather than
 * stampeding the auth-service.
 *
 * On refresh failure (network down, refresh token rotated by another
 * tab, etc.) we clear the session — the user is bounced to the login
 * screen by the route guard in App.tsx.
 */

import { refreshTokenRequest } from './authApi';
import { useAuthStore } from './authStore';

const EXPIRY_BUFFER_MS = 60 * 1000; // refresh when <60s left

let inFlightRefresh: Promise<string | null> | null = null;

function decodeJwtExpMs(jwt: string): number | null {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    // base64url-decode without dependencies
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(json) as { exp?: number };
    return typeof parsed.exp === 'number' ? parsed.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function performRefresh(refreshToken: string): Promise<string | null> {
  try {
    const resp = await refreshTokenRequest(refreshToken);
    const state = useAuthStore.getState();
    if (!state.user || !state.defaultOrgId || !state.defaultRole) {
      // Lost session shape — bail
      state.clearSession();
      return null;
    }
    state.setSession({
      accessToken: resp.access_token,
      refreshToken: resp.refresh_token,
      user: state.user,
      defaultOrgId: state.defaultOrgId,
      defaultRole: state.defaultRole,
    });
    return resp.access_token;
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[Forge] Token refresh failed; clearing session.', err);
    }
    useAuthStore.getState().clearSession();
    return null;
  }
}

/**
 * Resolve the access token to use for the next request, refreshing first
 * if it's near expiry. Coalesces concurrent calls into a single refresh.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const { accessToken, refreshToken } = useAuthStore.getState();
  if (!accessToken) return null;
  if (!refreshToken) return accessToken; // can't refresh; let the request fail naturally

  const expMs = decodeJwtExpMs(accessToken);
  const stale = expMs == null || expMs - Date.now() < EXPIRY_BUFFER_MS;
  if (!stale) return accessToken;

  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh(refreshToken).finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/**
 * If a request fails with what looks like an auth error, force one
 * refresh and run `retry()` with the new token. Returns the retry
 * result. If the failure isn't auth-related — or refresh fails — the
 * original error is rethrown.
 */
export async function refreshAndRetry<T>(
  err: unknown,
  retry: (newToken: string) => Promise<T>,
): Promise<T> {
  if (!isAuthError(err)) throw err;
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) throw err;

  const newToken = await performRefresh(refreshToken);
  if (!newToken) throw err;
  return retry(newToken);
}

const AUTH_ERROR_PATTERNS = [
  'jwtexpired',
  'jwt expired',
  'invalid-jwt',
  'invalid jwt',
  'unauthorized',
  'access denied',
  '401',
];

export function isAuthError(err: unknown): boolean {
  if (!err) return false;
  const blob = (err instanceof Error ? err.message : JSON.stringify(err)).toLowerCase();
  return AUTH_ERROR_PATTERNS.some((p) => blob.includes(p));
}
